/*
  ============================================================================
  rs-product.js — two small additions to the product page
  ============================================================================
  1. A sticky add-to-cart bar on phones, shown once the real button scrolls
     out of view.
  2. Dots under the phone media slider, to sit alongside Dawn's numeric
     counter.

  Both are additive. Neither touches Dawn's product-info.js or media-gallery.js,
  so variant switching, the gallery and add-to-cart behave exactly as before.
  ============================================================================
*/

(() => {
  'use strict';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* 1. Sticky add-to-cart bar                                           */
  /* ------------------------------------------------------------------ */

  class RsStickyAtc extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) return;

      /*
        The real submit button, which product-form.js has already wired up.
        Matched on .product-form__submit rather than [type="submit"] so the
        dynamic checkout button can never be picked up by mistake.
      */
      this.realButton = this.productInfo.querySelector('.rs-buy-buttons .product-form__submit');
      this.proxyButton = this.querySelector('[data-rs-sticky-submit]');
      this.watchTarget = this.productInfo.querySelector('.rs-buy-buttons');

      if (!this.realButton || !this.proxyButton || !this.watchTarget) return;

      /*
        Clicking the sticky button clicks the real one rather than submitting
        the form itself. That keeps the request going through product-form.js,
        so the cart drawer/notification and error handling still fire.
      */
      this.proxyButton.addEventListener('click', () => this.realButton.click());

      this.syncState();
      this.mirrorObserver = new MutationObserver(() => this.syncState());
      this.mirrorObserver.observe(this.realButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });

      /*
        A scroll handler rather than an IntersectionObserver. An observer only reports a
        CHANGE of intersecting state, so jumping from "scrolled past" straight back to the
        top (status-bar tap, anchor link) goes false to false and never fires, leaving the
        bar stuck on. Reading the button's position on each frame cannot miss a state.
        The bar shows only once the whole buy block has scrolled out of the TOP of the
        viewport, so it never appears while the button is still below the fold.
      */
      this.rafId = null;
      this.update = () => {
        this.rafId = null;
        this.classList.toggle('rs-sticky-atc--visible', this.watchTarget.getBoundingClientRect().bottom < 0);
      };
      this.onScroll = () => {
        if (!this.rafId) this.rafId = requestAnimationFrame(this.update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.mirrorObserver?.disconnect();
    }

    // Keep the sticky button's disabled state in step with the real one.
    syncState() {
      const disabled =
        this.realButton.hasAttribute('disabled') ||
        this.realButton.getAttribute('aria-disabled') === 'true';

      this.proxyButton.toggleAttribute('disabled', disabled);

      const label = this.realButton.querySelector('span');
      const proxyLabel = this.proxyButton.querySelector('[data-rs-sticky-label]');
      if (label && proxyLabel) proxyLabel.textContent = label.textContent.trim();
    }
  }

  if (!customElements.get('rs-sticky-atc')) {
    customElements.define('rs-sticky-atc', RsStickyAtc);
  }

  /* ------------------------------------------------------------------ */
  /* 2. Gallery dots                                                     */
  /* ------------------------------------------------------------------ */

  class RsGalleryDots extends HTMLElement {
    connectedCallback() {
      const gallery = this.closest('product-info')?.querySelector('media-gallery');
      this.slider = gallery?.querySelector('ul.slider--mobile');
      if (!this.slider) return;

      this.build();

      // Rebuild when product-info swaps the gallery on a variant change.
      this.galleryObserver = new MutationObserver(() => this.build());
      this.galleryObserver.observe(this.slider, { childList: true });

      this.onScroll = this.onScroll.bind(this);
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.galleryObserver?.disconnect();
      this.slider?.removeEventListener('scroll', this.onScroll);
    }

    build() {
      this.slides = Array.from(this.slider.querySelectorAll('li.slider__slide'));

      // A single image needs no dots.
      if (this.slides.length < 2) {
        this.innerHTML = '';
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.innerHTML = this.slides
        .map(
          (_, i) =>
            `<button type="button" class="rs-dot" data-index="${i}" aria-label="Go to image ${
              i + 1
            } of ${this.slides.length}"></button>`
        )
        .join('');

      this.dots = Array.from(this.querySelectorAll('.rs-dot'));
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => {
          const slide = this.slides[Number(dot.dataset.index)];
          this.slider.scrollTo({
            left: slide.offsetLeft - this.slider.offsetLeft,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        })
      );

      this.setActive(0);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        // Which slide is nearest the left edge of the viewport?
        const index = Math.round(this.slider.scrollLeft / (this.slider.scrollWidth / this.slides.length));
        this.setActive(Math.min(index, this.slides.length - 1));
      });
    }

    setActive(index) {
      this.dots?.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('rs-dot--active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('rs-gallery-dots')) {
    customElements.define('rs-gallery-dots', RsGalleryDots);
  }
})();
